import {
  useCreateUserFilterMutation,
  useDeleteUserFilterByIdMutation,
  useEditUserFilterByIdMutation,
  useLazyGetUserFilterByIdQuery,
  useLazyGetUserFiltersQuery,
} from 'store/services/flow/userFilters';
import { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import { FormControl, FormLabel, Select, MenuItem } from '@mui/material';
import { Cached, Delete as DeleteIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import type { FieldValues } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DrawerLayout } from '../DrawerLayout';
import { GenericFieldComponent } from './Filter/GenericField';
import { type AppliedFilter } from 'store/entities/drawer';
import { selectDrawer } from 'store/entities/drawer/selectors';
import { setFilters, setSavedFilters } from 'store/entities/drawer/actions';
import { extractFilterValues, isValueEmpty } from 'utils/filterFunctions';

import { TextField } from 'components/mui/common/TextField';
import type { TABLE_CELL_TYPE } from 'types/enums';
import { Button } from 'components/mui/common/Button';
import { Switch } from 'components/mui/common/Switch';
import type { FilterColumnValue, FilterValue, UserFilter } from 'types/userFilters';
import type { ViewFormItemDefinition } from 'types/views';
import { LoadingContainer } from 'components/common/LoadingContainer';
import { toast } from 'react-toastify';
import type { UserAgendaTableDataSchema } from 'types';
import { trackFilterApplied } from 'utils/posthog/metrics';
import { useLogin } from 'contexts';
import {
  FilterActionsWrapper,
  FooterWrapper,
  SavedFiltersWrapper,
  SavedWrapper,
  StyledButton,
  StyledButtonGroup,
  StyledIconButton,
  StyledResetButton,
  StyledSaveButton,
  StyledSaveIcon,
} from './Filter.styles';

interface DataSchema {
  label: string;
  formItemDefinition?: ViewFormItemDefinition;
  type: TABLE_CELL_TYPE;
  translationKey?: string;
  customKey: string;
  dateMask?: string;
  originalType?: string;
  columnId?: string;
}

interface FilterState {
  filterName: string;
  selectedSavedFilter: string;
  editFilter: UserFilter | null;
}

interface ExtractedFilterValue {
  formItemId: string;
  values?: string[];
  fromValue?: string | number;
  toValue?: string | number;
  formItemType?: string;
  referencedFormItemId?: string;
}

const defaultFilterState = {
  filtersToApply: [],
  selectedSavedFilter: '',
  filterName: '',
  editFilter: {
    isDefault: false,
    isGlobal: false,
    name: '',
    userFilterId: '',
    values: [],
  },
};

export const FilterDrawer = memo(() => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const drawer = useSelector(selectDrawer());
  const { permissions } = useLogin();

  const form = useForm();
  const { control, handleSubmit, reset, setValue } = form;

  const [filterState, setFilterState] = useState<FilterState>(defaultFilterState);
  const isResettingRef = useRef(false);
  const hasAdminPermission = permissions?.templateAdmin;

  const [getSavedFilters, { isFetching }] = useLazyGetUserFiltersQuery();
  const [triggerCreateUserFilter] = useCreateUserFilterMutation();
  const [triggerEditUserFilter] = useEditUserFilterByIdMutation();
  const [triggerDeleteUserFilter] = useDeleteUserFilterByIdMutation();
  const [getFilterById] = useLazyGetUserFilterByIdQuery();

  const applyFilters = (data: Record<string, FieldValues>) => {
    const filters = Object.entries(data).reduce<AppliedFilter[]>((acc, [key, value]) => {
      if (isValueEmpty(value)) return acc;
      const schemaItem = Object.values(drawer.dataSchema as DataSchema[]).find((item) => item.label === key);
      if (schemaItem) {
        const formItemType = schemaItem.originalType || schemaItem.type || '';
        let processedValue: AppliedFilter['value'] = value;

        if (formItemType === 'Date' && value instanceof Date) {
          const formattedDate = value.toISOString();
          if (formattedDate) {
            processedValue = formattedDate;
          }
        } else if (formItemType === 'Date' && typeof value === 'object' && value !== null) {
          if ('fromDate' in value && value.fromDate instanceof Date) {
            processedValue = {
              ...value,
              fromDate: value.fromDate.toISOString(),
            };
          }
          if ('toDate' in value && value.toDate instanceof Date) {
            processedValue = {
              ...processedValue,
              toDate: value.toDate.toISOString(),
            };
          }
        }

        acc.push({
          formItemId: drawer.type === 'table' ? schemaItem.columnId || key : key,
          formItemType,
          value: processedValue,
        });
      }
      return acc;
    }, []);

    return filters;
  };

  const handleFormSubmit = handleSubmit((data) => {
    const filters = applyFilters(data);
    dispatch(setFilters({ [drawer.entity]: filters }));
    try {
      trackFilterApplied({ process: 'filter', step: 'apply', agendaId: drawer.agendaId, viewId: drawer.viewId, count: filters.length });
    } catch {
      /*noop*/
    }
  });

  const handleReset = () => {
    isResettingRef.current = true;

    const defaultValues: Record<string, null> = {};
    if (drawer?.dataSchema) {
      Object.values(drawer.dataSchema as UserAgendaTableDataSchema[]).forEach((item) => {
        if (item.label) {
          defaultValues[item.label] = null;
        }
      });
    }

    reset(defaultValues);
    setFilterState(defaultFilterState);
    dispatch(setFilters({ [drawer.entity]: undefined }));
    setTimeout(() => {
      isResettingRef.current = false;
    }, 100);
  };

  const refreshSavedFilters = async () => {
    const { data } = await getSavedFilters({
      viewId: drawer.viewId ?? '',
      ...(drawer.type === 'table' ? { tableId: drawer.tableId ?? '' } : { agendaId: drawer.agendaId ?? '' }),
      pageSize: 100,
      page: 1,
      orderDesc: false,
      search: '',
      orderBy: '' as never,
    });
    dispatch(setSavedFilters({ [drawer.entity]: data?.filters?.items ?? [] }));
  };

  const handleSaveFilterSubmit = handleSubmit((data) => {
    if (!filterState?.editFilter?.name) return;

    const isEditingSavedFilter = drawer.savedFilters?.[drawer.entity]?.find((filter) => filter.userFilterId === filterState.selectedSavedFilter);
    if (isEditingSavedFilter) return handleEditSave(data);
    return handleSaveFilter(data);
  });

  const mapToColumnValues = (extractedValues: ExtractedFilterValue[]) => {
    return extractedValues.map((val) => {
      return {
        columnId: val.formItemId,
        values: val.values ?? [],
        fromValue: val.fromValue,
        toValue: val.toValue,
      };
    });
  };

  const handleEditSave = async (data: FieldValues) => {
    if (!filterState.editFilter) return;

    const filters = applyFilters(data);

    const extractedValues = extractFilterValues(filters, drawer.referencedFormItemsMap);

    const basePayload = {
      userFilterId: filterState.editFilter.userFilterId,
      name: filterState.editFilter.name,
      isGlobal: filterState.editFilter?.isGlobal,
      isDefault: filterState.editFilter?.isDefault,
    };

    let payload;

    if (drawer.type === 'table') {
      payload = {
        ...basePayload,
        values: [] as FilterValue[],
        columnValues: mapToColumnValues(extractedValues),
      };
    } else {
      payload = {
        ...basePayload,
        values: extractedValues,
      };
    }

    const res = await triggerEditUserFilter(payload);

    if (!('error' in res)) {
      toast.success(t('filters.updated'));
    } else return toast.error(t('common.error'));

    dispatch(setFilters({ [drawer.entity]: filters }));
    await refreshSavedFilters();
  };

  const handleSaveFilter = async (data: FieldValues) => {
    const filters = applyFilters(data);

    const extractedValues = extractFilterValues(filters, drawer.referencedFormItemsMap);

    const basePayload = {
      viewId: drawer.viewId ?? '',
      name: filterState.editFilter?.name || 'Filter',
      isDefault: filterState.editFilter?.isDefault || false,
      isGlobal: filterState.editFilter?.isGlobal || false,
    };

    let payload;

    if (drawer.type === 'table') {
      payload = {
        ...basePayload,
        tableId: drawer.tableId ?? '',
        values: [] as FilterValue[],
        columnValues: mapToColumnValues(extractedValues),
      };
    } else {
      payload = {
        ...basePayload,
        agendaId: drawer.agendaId ?? '',
        values: extractedValues,
      };
    }

    const res = await triggerCreateUserFilter(payload);

    if (!('error' in res)) {
      toast.success(t('filters.created'));
      setFilterState((prev) => ({
        ...prev,
        selectedSavedFilter: res.data.userFilterId,
        editFilter: {
          ...prev.editFilter!,
          userFilterId: res.data.userFilterId,
          isDefault: res.data.isDefault,
          isGlobal: res.data.isGlobal,
        },
      }));
    } else return toast.error(t('common.error'));

    dispatch(setFilters({ [drawer.entity]: filters }));
    await refreshSavedFilters();
  };

  const handleDeleteClick = async (event: React.MouseEvent, filterId: string) => {
    event.stopPropagation();
    const res = await triggerDeleteUserFilter({ userFilterId: filterId });

    if (!('error' in res)) {
      toast.success(t('filters.deleted'));
    } else return toast.error(t('common.error'));

    await refreshSavedFilters();
    if (filterState.selectedSavedFilter === filterId) {
      handleReset();
    }
  };

  const makeFiltersToSet = (filters: FilterValue[] | FilterColumnValue[]) => {
    return filters.map((value) => {
      const item = value as FilterValue & FilterColumnValue;
      const lookupKey = item.referencedFormItemId || item.columnId || item.formItemId;
      const schemaItem = Object.values(drawer.dataSchema as DataSchema[]).find((schema) => schema.label === lookupKey);

      const formItemIdToUse = lookupKey;
      const formItemType = schemaItem?.originalType || item.formItemType || '';

      let processedValue;

      if (formItemType === 'Date' || formItemType === 'Number') {
        const isNumber = formItemType === 'Number';
        const fromKey = isNumber ? 'fromNumber' : 'fromDate';
        const toKey = isNumber ? 'toNumber' : 'toDate';

        const parseValue = (val: string) => (isNumber ? parseFloat(val) : val);

        if (item.fromValue || item.toValue) {
          processedValue = {
            ...(item.fromValue && { [fromKey]: parseValue(item.fromValue) }),
            ...(item.toValue && { [toKey]: parseValue(item.toValue) }),
          };
        } else if (item.values?.length === 1) {
          const singleValue = parseValue(item.values[0]);
          processedValue = {
            [fromKey]: singleValue,
            [toKey]: singleValue,
          };
        }
      } else if (item.values && item.values.length > 1) {
        processedValue = item.values.map((v) => ({ value: v }));
      } else if (item.values && item.values.length > 0) {
        processedValue = item.values[0];
      }

      return {
        formItemId: formItemIdToUse,
        formItemType,
        value: processedValue,
        referencedFormItemId: item.referencedFormItemId,
      };
    });
  };

  const handleSavedFilterChange = async (event: SelectChangeEvent<string>) => {
    const filterId = event.target.value;

    isResettingRef.current = true;

    const defaultValues: Record<string, null> = {};
    if (drawer?.dataSchema) {
      Object.values(drawer.dataSchema as UserAgendaTableDataSchema[]).forEach((item) => {
        if (item.label) {
          defaultValues[item.label] = null;
        }
      });
    }

    reset(defaultValues);
    setFilterState(defaultFilterState);

    if (!filterId) {
      isResettingRef.current = false;
      return;
    }

    const { data: selectedFilter } = await getFilterById({ userFilterId: filterId });
    if (!selectedFilter) {
      isResettingRef.current = false;
      return;
    }

    const filtersToSet = makeFiltersToSet(drawer.type === 'table' ? selectedFilter.columnValues || [] : selectedFilter.values);

    setFilterState({
      filterName: selectedFilter.name,
      selectedSavedFilter: filterId,
      editFilter: selectedFilter,
    });

    isResettingRef.current = false;
    setFiltersToApply(filtersToSet as unknown as AppliedFilter[]);
  };

  const setFiltersToApply = useCallback(
    (filters: AppliedFilter[]) => {
      if (isResettingRef.current) {
        return;
      }

      setFilterState((prev) => ({
        ...prev,
        filtersToApply: filters || [],
      }));

      if (filters && filters.length > 0) {
        filters.forEach((filter) => {
          setValue(filter.formItemId, filter.value);
        });
      }
    },
    [setValue]
  );

  const GenericField = useMemo(() => {
    if (!drawer?.dataSchema) return null;

    return Object.values(drawer.dataSchema as UserAgendaTableDataSchema[]).map((item) => {
      if (!item.type) return null;
      return <GenericFieldComponent key={item.label} item={item} control={control} />;
    });
  }, [drawer?.dataSchema, control]);

  useEffect(() => {
    const defaultFilter = drawer?.savedFilters?.[drawer.entity]?.find((filter) => filter.isDefault);

    setFilterState((prev) => ({
      ...prev,
      selectedSavedFilter: defaultFilter?.userFilterId ?? '',
      editFilter: defaultFilter ?? null,
    }));

    if (!defaultFilter) return;

    const fetchInitialFilter = async () => {
      const { data } = await getFilterById({ userFilterId: defaultFilter.userFilterId });
      if (!data) return;

      const valuesToMap = drawer.type === 'table' ? data.columnValues || [] : data.values || [];

      const filtersToSet = makeFiltersToSet(valuesToMap);

      setFiltersToApply(filtersToSet as unknown as AppliedFilter[]);
    };

    fetchInitialFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isResettingRef.current) {
      return;
    }

    const entityFilters = drawer.appliedFilters?.[drawer.entity];
    if (entityFilters && entityFilters.length > 0) {
      setFiltersToApply(entityFilters as unknown as AppliedFilter[]);
    } else if (!drawer.appliedFilters || Object.keys(drawer.appliedFilters).length === 0 || (entityFilters && entityFilters.length === 0)) {
      reset();
    }
  }, [drawer.appliedFilters, drawer.entity, setFiltersToApply, reset]);

  const filterPermissions = useMemo(() => {
    const selectedFilter = drawer.savedFilters?.[drawer.entity]?.find((f) => f.userFilterId === filterState.selectedSavedFilter);

    const canManage = hasAdminPermission || (selectedFilter ? !selectedFilter.isGlobal : true);

    const showDefaultSwitch = !filterState.editFilter?.isGlobal;

    return {
      canManage,
      showDefaultSwitch,
      isGlobal: !!filterState.editFilter?.isGlobal,
    };
  }, [drawer.savedFilters, drawer.entity, filterState.selectedSavedFilter, hasAdminPermission, filterState.editFilter?.isGlobal]);

  return (
    <LoadingContainer loading={isFetching}>
      <DrawerLayout
        title={t('filters.title')}
        footer={
          <FooterWrapper>
            <StyledResetButton variant="outlined" fullWidth onClick={handleReset} startIcon={<Cached />}>
              {t('filters.reset')}
            </StyledResetButton>
            {drawer.canSave ? (
              <StyledButtonGroup fullWidth variant="contained">
                <Button fullWidth onClick={handleFormSubmit}>
                  {t('filters.apply')}
                </Button>
                {filterPermissions.canManage && (hasAdminPermission || !filterState.editFilter?.isGlobal) && (
                  <StyledSaveButton onClick={handleSaveFilterSubmit}>
                    <StyledSaveIcon fontSize="small" />
                  </StyledSaveButton>
                )}
              </StyledButtonGroup>
            ) : (
              <StyledButton variant="contained" fullWidth onClick={handleFormSubmit}>
                {t('filters.apply')}
              </StyledButton>
            )}
          </FooterWrapper>
        }>
        <FormProvider {...form}>
          <FormControl fullWidth margin="normal">
            {drawer.savedFilters && drawer.savedFilters?.[drawer.entity]?.length > 0 && (
              <>
                <FormLabel>{t('filters.savedFilters')}</FormLabel>
                <SavedFiltersWrapper>
                  <Select
                    MenuProps={{
                      disablePortal: true,
                    }}
                    value={filterState.selectedSavedFilter}
                    onChange={handleSavedFilterChange}
                    displayEmpty
                    fullWidth>
                    <MenuItem value="">
                      <em>{t('filters.selectSavedFilter')}</em>
                    </MenuItem>
                    {drawer.savedFilters?.[drawer.entity].map((filter) => (
                      <MenuItem key={filter.userFilterId} value={filter.userFilterId}>
                        {filter.isDefault ? `${t('filters.saveFilter.default')} - ` : ''}
                        {filter.name}
                      </MenuItem>
                    ))}
                  </Select>

                  {filterState.selectedSavedFilter && filterPermissions.canManage && (
                    <FilterActionsWrapper>
                      {(hasAdminPermission || !filterState.editFilter?.isGlobal) && (
                        <StyledIconButton size="small" onClick={(e) => handleDeleteClick(e, filterState.selectedSavedFilter)}>
                          <DeleteIcon fontSize="small" />
                        </StyledIconButton>
                      )}
                    </FilterActionsWrapper>
                  )}
                </SavedFiltersWrapper>
              </>
            )}
            {drawer.canSave && filterPermissions.canManage && (
              <SavedWrapper>
                <div>
                  <FormLabel>{t('filters.saveFilter.name')}</FormLabel>
                  <TextField
                    fullWidth
                    value={filterState.editFilter?.name}
                    disabled={filterState.editFilter?.isGlobal && !hasAdminPermission}
                    onChange={(e) => {
                      setFilterState((prev) => ({
                        ...prev,
                        editFilter: {
                          ...prev.editFilter!,
                          name: e.target.value,
                        },
                      }));
                    }}
                  />
                </div>
                {filterPermissions.showDefaultSwitch && (
                  <Switch
                    key={filterState.editFilter?.userFilterId}
                    label={t('filters.saveFilter.default')}
                    defaultChecked={filterState.editFilter?.isDefault || false}
                    disabled={(filterState.editFilter?.isGlobal && !hasAdminPermission) || filterState.editFilter?.isGlobal}
                    onChange={(e) => {
                      const isDefault = e.target.checked;
                      setFilterState((prev) => ({
                        ...prev,
                        editFilter: prev.editFilter
                          ? {
                              ...prev.editFilter,
                              isDefault,
                              isGlobal: isDefault ? false : prev.editFilter.isGlobal,
                            }
                          : null,
                      }));
                    }}
                  />
                )}
                {hasAdminPermission && (
                  <Switch
                    key={`isGlobal-${filterState.editFilter?.userFilterId}`}
                    label={t('filters.saveFilter.global')}
                    defaultChecked={filterState.editFilter?.isGlobal || false}
                    disabled={!!filterState.editFilter?.userFilterId || filterState.editFilter?.isDefault}
                    onChange={(e) => {
                      const isGlobal = e.target.checked;
                      setFilterState((prev) => ({
                        ...prev,
                        editFilter: prev.editFilter
                          ? {
                              ...prev.editFilter,
                              isGlobal,
                              isDefault: isGlobal ? false : prev.editFilter.isDefault,
                            }
                          : null,
                      }));
                    }}
                  />
                )}
              </SavedWrapper>
            )}
          </FormControl>
          {GenericField}
        </FormProvider>
      </DrawerLayout>
    </LoadingContainer>
  );
});
